import React, { useMemo } from 'react';
import { Save } from 'lucide-react';

export type SpreadMode = 'fixed' | 'dynamic' | 'banded';
export type FeeConfig = {
  spread_mode: SpreadMode;
  spread_fixed_rate: number;
  spread_dynamic_max_rate: number;
  spread_dynamic_min_rate: number;
  spread_dynamic_low_probability: number;
  spread_dynamic_high_probability: number;
  spread_banded_config: Array<{ min_price: number; max_price: number; rate: number }>;
  performance_fee_enabled: boolean;
  performance_fee_rate: number;
  creator_commission_rate: number;
  affiliate_commission_rate: number;
  /** Platform-wide minimum stake / notional (ZAR). */
  min_trade_zar: number;
};

export const defaultFeeConfig: FeeConfig = {
  spread_mode: 'fixed',
  spread_fixed_rate: 0.02,
  spread_dynamic_max_rate: 0.15,
  spread_dynamic_min_rate: 0.05,
  spread_dynamic_low_probability: 0.5,
  spread_dynamic_high_probability: 0.85,
  spread_banded_config: [
    { min_price: 0.0, max_price: 0.19, rate: 0.03 },
    { min_price: 0.2, max_price: 0.49, rate: 0.025 },
    { min_price: 0.5, max_price: 0.79, rate: 0.02 },
    { min_price: 0.8, max_price: 1.0, rate: 0.015 },
  ],
  performance_fee_enabled: true,
  performance_fee_rate: 0.15,
  creator_commission_rate: 0.05,
  affiliate_commission_rate: 0.05,
  min_trade_zar: 10,
};

export function toPercent(n: number): number {
  return Math.round(n * 10000) / 100;
}

export function fromPercent(n: number): number {
  return Math.round((n / 100) * 10000) / 10000;
}

function interpolateSpread(prob: number, cfg: FeeConfig): number {
  if (cfg.spread_mode === 'fixed') return cfg.spread_fixed_rate;
  if (cfg.spread_mode === 'dynamic') {
    const low = Math.min(cfg.spread_dynamic_low_probability, cfg.spread_dynamic_high_probability);
    const high = Math.max(cfg.spread_dynamic_low_probability, cfg.spread_dynamic_high_probability);
    if (prob <= low) return cfg.spread_dynamic_max_rate;
    if (prob >= high) return cfg.spread_dynamic_min_rate;
    const span = Math.max(1e-12, high - low);
    return cfg.spread_dynamic_max_rate
      - ((cfg.spread_dynamic_max_rate - cfg.spread_dynamic_min_rate) * ((prob - low) / span));
  }
  for (const band of cfg.spread_banded_config) {
    if (prob >= band.min_price && (prob < band.max_price || band.max_price === 1.0)) return band.rate;
  }
  return cfg.spread_fixed_rate;
}

export function costPerShareAt(prob: number, cfg: FeeConfig): number {
  return prob * (1 + interpolateSpread(prob, cfg));
}

export interface AdminFeesPanelProps {
  /** Saved config from DB (used for dirty check + discard). */
  saved: FeeConfig;
  /** Draft config (user edits). */
  draft: FeeConfig;
  onChange: (next: FeeConfig) => void;
  /** Triggered when Save is clicked. */
  onSave: () => void | Promise<void>;
  /** If true, whole panel is read-only. */
  readOnly?: boolean;
  /** Shown above panel (e.g. market status lock notice). */
  notice?: React.ReactNode;
  /** Displayed message after save. */
  successMessage?: string | null;
  errorMessage?: string | null;
  saving?: boolean;
  /** Label for save button. Defaults to "Save". */
  saveLabel?: string;
  /** When true, hides the platform minimum trade floor (only for global Revenue & Fees page). */
  hideMinTradeFloor?: boolean;
}

export function AdminFeesPanel({
  saved,
  draft,
  onChange,
  onSave,
  readOnly = false,
  notice,
  successMessage,
  errorMessage,
  saving = false,
  saveLabel = 'Save',
  hideMinTradeFloor = false,
}: AdminFeesPanelProps) {
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const commissionInvalid = draft.creator_commission_rate + draft.affiliate_commission_rate > 1;
  const dynamicRatesInvalid =
    draft.spread_mode === 'dynamic' && draft.spread_dynamic_min_rate >= draft.spread_dynamic_max_rate;
  const dynamicThresholdsInvalid =
    draft.spread_mode === 'dynamic' &&
    draft.spread_dynamic_low_probability >= draft.spread_dynamic_high_probability;
  const minTradeInvalid = draft.min_trade_zar < 1 || draft.min_trade_zar > 1000;
  const hasValidationError =
    commissionInvalid || dynamicRatesInvalid || dynamicThresholdsInvalid || minTradeInvalid;

  const wayjaNetPct = useMemo(
    () => Math.max(0, 1 - draft.creator_commission_rate - draft.affiliate_commission_rate),
    [draft],
  );

  const setDraft = (next: FeeConfig) => {
    if (readOnly) return;
    onChange(next);
  };

  const discard = () => onChange(saved);

  return (
    <div className="bg-white border rounded-lg p-5 space-y-6">
      {notice}

      {errorMessage && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">{errorMessage}</div>
      )}
      {successMessage && (
        <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded text-sm">
          {successMessage}
        </div>
      )}

      <fieldset disabled={readOnly} className={readOnly ? 'opacity-70 pointer-events-none space-y-6' : 'space-y-6'}>
        {!hideMinTradeFloor && (
          <div className="border-b border-gray-100 pb-4">
            <h2 className="font-semibold mb-1">Minimum trade (hard floor)</h2>
            <p className="text-sm text-gray-600 mb-3">
              Smallest stake for pool buys and LMSR buys; also the minimum gross notion for partial LMSR sells unless
              exiting the full position. Enforced in the app and API.
            </p>
            <label className="text-sm font-medium block">Amount (ZAR)</label>
            <input
              type="number"
              min={1}
              max={1000}
              step={1}
              value={draft.min_trade_zar}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  min_trade_zar: Number(e.target.value),
                })
              }
              className="border rounded p-2 w-32 tabular-nums"
            />
            {minTradeInvalid && (
              <p className="text-sm text-red-600 mt-1">Must be between R1 and R1000.</p>
            )}
          </div>
        )}

        {/* Spread mode */}
        <div>
          <h2 className="font-semibold mb-2">Spread mode</h2>
          <div className="grid grid-cols-3 gap-3">
            {(['fixed', 'dynamic', 'banded'] as SpreadMode[]).map((mode) => (
              <button
                type="button"
                key={mode}
                className={`p-3 border rounded ${
                  draft.spread_mode === mode ? 'border-wayja-teal border-2' : 'border-gray-300'
                }`}
                onClick={() => setDraft({ ...draft, spread_mode: mode })}
              >
                {mode === 'banded' ? 'Price-banded' : mode[0].toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {draft.spread_mode === 'fixed' && (
          <div>
            <label className="text-sm font-medium block">Fixed rate: {toPercent(draft.spread_fixed_rate)}%</label>
            <input
              type="range"
              min={0}
              max={15}
              step={1}
              value={toPercent(draft.spread_fixed_rate)}
              onChange={(e) => setDraft({ ...draft, spread_fixed_rate: fromPercent(Number(e.target.value)) })}
              className="w-full"
            />
            <p className="text-xs text-gray-500">
              Cost per share on a 60c market: R{costPerShareAt(0.6, draft).toFixed(3)}
            </p>
          </div>
        )}

        {draft.spread_mode === 'dynamic' && (
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium block">Max rate: {toPercent(draft.spread_dynamic_max_rate)}%</label>
              <input
                type="range"
                min={0}
                max={15}
                step={1}
                value={toPercent(draft.spread_dynamic_max_rate)}
                onChange={(e) =>
                  setDraft({ ...draft, spread_dynamic_max_rate: fromPercent(Number(e.target.value)) })
                }
                className="w-full"
              />
            </div>
            <div>
              <label className="text-sm font-medium block">Min rate: {toPercent(draft.spread_dynamic_min_rate)}%</label>
              <input
                type="range"
                min={0}
                max={15}
                step={1}
                value={toPercent(draft.spread_dynamic_min_rate)}
                onChange={(e) =>
                  setDraft({ ...draft, spread_dynamic_min_rate: fromPercent(Number(e.target.value)) })
                }
                className="w-full"
              />
            </div>
            {dynamicRatesInvalid && (
              <p className="text-sm text-red-600">Min rate must be lower than max rate.</p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-gray-100">
              <div>
                <label className="text-sm font-medium block">
                  Below this probability → use max rate:{' '}
                  <span className="tabular-nums">{Math.round(draft.spread_dynamic_low_probability * 100)}%</span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={99}
                  step={1}
                  value={Math.round(draft.spread_dynamic_low_probability * 100)}
                  onChange={(e) =>
                    setDraft({ ...draft, spread_dynamic_low_probability: Number(e.target.value) / 100 })
                  }
                  className="w-full"
                />
                <p className="text-xs text-gray-500">
                  At or below {Math.round(draft.spread_dynamic_low_probability * 100)}% fair probability, the
                  full max rate ({toPercent(draft.spread_dynamic_max_rate)}%) is charged.
                </p>
              </div>
              <div>
                <label className="text-sm font-medium block">
                  Above this probability → use min rate:{' '}
                  <span className="tabular-nums">{Math.round(draft.spread_dynamic_high_probability * 100)}%</span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={99}
                  step={1}
                  value={Math.round(draft.spread_dynamic_high_probability * 100)}
                  onChange={(e) =>
                    setDraft({ ...draft, spread_dynamic_high_probability: Number(e.target.value) / 100 })
                  }
                  className="w-full"
                />
                <p className="text-xs text-gray-500">
                  At or above {Math.round(draft.spread_dynamic_high_probability * 100)}% fair probability, the
                  min rate ({toPercent(draft.spread_dynamic_min_rate)}%) is charged.
                </p>
              </div>
            </div>
            {dynamicThresholdsInvalid && (
              <p className="text-sm text-red-600">
                The "max rate" probability must be lower than the "min rate" probability.
              </p>
            )}

            <div className="grid grid-cols-3 gap-2 text-xs pt-2">
              {[
                Math.max(0.05, draft.spread_dynamic_low_probability - 0.1),
                (draft.spread_dynamic_low_probability + draft.spread_dynamic_high_probability) / 2,
                Math.min(0.95, draft.spread_dynamic_high_probability + 0.05),
              ].map((p, i) => (
                <div key={i} className="border rounded p-2 bg-gray-50">
                  <div>{Math.round(p * 100)}% probability</div>
                  <div className="font-semibold">Cost/share: R{costPerShareAt(p, draft).toFixed(3)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {draft.spread_mode === 'banded' && (
          <div className="space-y-2">
            {draft.spread_banded_config.map((band, i) => (
              <div key={`${band.min_price}-${band.max_price}`} className="grid grid-cols-3 gap-2 items-center">
                <div className="text-sm">
                  {band.min_price.toFixed(2)} - {band.max_price.toFixed(2)}
                </div>
                <input
                  type="number"
                  min={0}
                  max={15}
                  step={0.1}
                  value={toPercent(band.rate)}
                  onChange={(e) => {
                    const next = [...draft.spread_banded_config];
                    next[i] = { ...next[i], rate: fromPercent(Number(e.target.value)) };
                    setDraft({ ...draft, spread_banded_config: next });
                  }}
                  className="border rounded p-2"
                />
                <div className="text-sm text-gray-600">
                  Example at {((band.min_price + band.max_price) / 2).toFixed(2)}: R
                  {(((band.min_price + band.max_price) / 2) * (1 + band.rate)).toFixed(3)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div>
          <h2 className="font-semibold mb-2">Performance fee</h2>
          <label className="flex items-center gap-2 text-sm mb-2">
            <input
              type="checkbox"
              checked={draft.performance_fee_enabled}
              onChange={(e) => setDraft({ ...draft, performance_fee_enabled: e.target.checked })}
            />
            {draft.performance_fee_enabled ? 'Active' : 'Disabled'}
          </label>
          <label className="text-sm font-medium block">Rate: {toPercent(draft.performance_fee_rate)}%</label>
          <input
            type="range"
            min={0}
            max={20}
            step={1}
            disabled={!draft.performance_fee_enabled}
            value={toPercent(draft.performance_fee_rate)}
            onChange={(e) => setDraft({ ...draft, performance_fee_rate: fromPercent(Number(e.target.value)) })}
            className="w-full"
          />
        </div>

        <div>
          <h2 className="font-semibold mb-2">Creator / Affiliate commission</h2>
          <label className="text-sm font-medium block">
            Creator: {toPercent(draft.creator_commission_rate)}%
          </label>
          <input
            type="range"
            min={0}
            max={15}
            step={1}
            value={toPercent(draft.creator_commission_rate)}
            onChange={(e) => setDraft({ ...draft, creator_commission_rate: fromPercent(Number(e.target.value)) })}
            className="w-full"
          />
          <label className="text-sm font-medium block">
            Affiliate: {toPercent(draft.affiliate_commission_rate)}%
          </label>
          <input
            type="range"
            min={0}
            max={15}
            step={1}
            value={toPercent(draft.affiliate_commission_rate)}
            onChange={(e) => setDraft({ ...draft, affiliate_commission_rate: fromPercent(Number(e.target.value)) })}
            className="w-full"
          />
          {commissionInvalid && (
            <p className="text-sm text-red-600">Creator + affiliate cannot exceed 100%.</p>
          )}
          <p className="text-sm mt-2">
            Wayja net of fee split: <span className="font-semibold">{toPercent(wayjaNetPct)}%</span>
          </p>
        </div>
      </fieldset>

      {!readOnly && (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="px-4 py-2 rounded border disabled:opacity-50"
            onClick={discard}
            disabled={!dirty || saving}
          >
            Discard changes
          </button>
          <button
            type="button"
            className="px-4 py-2 rounded bg-wayja-teal text-white disabled:opacity-50 inline-flex items-center gap-2"
            onClick={() => void onSave()}
            disabled={saving || hasValidationError || !dirty}
          >
            <Save className="w-4 h-4" />
            {saveLabel}
          </button>
        </div>
      )}
    </div>
  );
}
